<script setup>
import axios from 'axios';
import { computed, ref, watch } from 'vue';
import { accountTypeLabel } from '../format';
import { extractApiError } from '../apiError';
import { t } from '../i18n';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'imported']);

const loading = ref(false);
const importing = ref(false);
const error = ref('');
const accounts = ref([]);
const selected = ref([]);

const allSelected = computed(() => accounts.value.length > 0 && selected.value.length === accounts.value.length);

function toggleAll() {
  selected.value = allSelected.value ? [] : accounts.value.map((a) => a.id);
}

async function loadAccounts() {
  loading.value = true;
  error.value = '';
  accounts.value = [];
  selected.value = [];
  try {
    const { data } = await axios.get('/portfolios/tinvest/accounts');
    accounts.value = data.accounts;
    selected.value = data.accounts.map((a) => a.id);
  } catch (e) {
    if (e?.response?.status !== 401) {
      error.value = extractApiError(e, t('import.accountsFailed'));
    }
  } finally {
    loading.value = false;
  }
}

async function submit() {
  if (selected.value.length === 0) {
    return;
  }
  importing.value = true;
  error.value = '';
  try {
    const { data } = await axios.post('/portfolios/tinvest/import', { account_ids: selected.value });
    emit('imported', data);
  } catch (e) {
    if (e?.response?.status !== 401) {
      error.value = extractApiError(e, t('import.failed'));
    }
  } finally {
    importing.value = false;
  }
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      loadAccounts();
    }
  },
  { immediate: true }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[90] flex items-end justify-center bg-black/60 sm:items-center sm:p-4"
      @click.self="emit('close')"
    >
      <div
        class="w-full rounded-t-3xl sm:max-w-md bg-slate-900 p-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] shadow-2xl max-sm:max-h-[88dvh] max-sm:overflow-y-auto sm:rounded-xl sm:border sm:border-slate-700 sm:pb-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="import-portfolios-title"
      >
        <h3
          id="import-portfolios-title"
          class="text-base font-semibold text-slate-100"
        >
          {{ t('import.title') }}
        </h3>
        <p class="mt-1 text-xs text-slate-500">
          {{ t('import.hint') }}
        </p>

        <p
          v-if="loading"
          class="mt-4 text-sm text-slate-400"
        >
          {{ t('import.loadingAccounts') }}
        </p>

        <template v-else-if="accounts.length > 0">
          <label class="mt-4 flex items-center gap-2 border-b border-slate-800 pb-2 text-sm text-slate-300">
            <input
              type="checkbox"
              class="accent-emerald-500"
              :checked="allSelected"
              @change="toggleAll"
            >
            {{ t('import.allAccounts') }}
          </label>
          <ul class="mt-2 max-h-72 space-y-1 overflow-y-auto">
            <li
              v-for="account in accounts"
              :key="account.id"
            >
              <label class="flex cursor-pointer items-center gap-3 rounded px-1 py-1.5 hover:bg-slate-800/60">
                <input
                  v-model="selected"
                  type="checkbox"
                  class="accent-emerald-500"
                  :value="account.id"
                >
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm text-slate-200">{{ account.name || account.id }}</span>
                  <span class="block text-xs text-slate-500">
                    {{ accountTypeLabel(account.type) }} · {{ account.id }}
                    <span
                      v-if="account.imported"
                      class="text-emerald-500"
                    >· {{ t('import.loaded') }}</span>
                  </span>
                </span>
              </label>
            </li>
          </ul>
        </template>

        <p
          v-else-if="!error"
          class="mt-4 text-sm text-slate-400"
        >
          {{ t('import.noAccounts') }}
        </p>

        <p
          v-if="error"
          class="mt-4 text-sm text-rose-400"
        >
          {{ error }}
        </p>

        <div class="mt-6 flex justify-end gap-2">
          <button
            type="button"
            class="rounded bg-slate-800 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700"
            @click="emit('close')"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="button"
            class="rounded bg-emerald-600 px-4 py-2 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
            :disabled="loading || importing || selected.length === 0"
            @click="submit"
          >
            {{ importing ? t('common.loading') : t('import.submit', { count: selected.length }) }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
